import { useTranslator } from "@eo-locale/react";
import { useEffect } from "react";

import {
  Box,
  Flex,
  Heading,
  Progress,
  Tag,
  Text
} from "@/storybook-components";
import useBudget from "@/hooks/useBudget";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { fetchUserBudgetAllocations } from "@/redux/features/brainstormers/thunk";

const BudgetAllocation = ({ children }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { brainstormer } = useAppSelector((state) => state.brainstormers);
  const unitCode = brainstormer?.baCategoryConfigAttributes?.unitCode || "";

  useEffect(() => {
    dispatch(fetchUserBudgetAllocations());
  }, []);

  const { totalBudget, totalAllocated, remainingBudget, formatBudget } =
    useBudget();

  return (
    <Flex
      direction={{ base: "column", lg: "column-reverse" }}
      position="relative"
      w="100%"
    >
      {totalAllocated || remainingBudget ? (
        <Box
          position="sticky"
          top={{ base: 2, lg: "auto" }}
          bottom={{ base: "auto", lg: 2 }}
          maxW={{ base: "100%", lg: "900px" }}
          borderRadius="md"
          zIndex={10}
          bg="gray.50"
          mx="auto"
          w="100%"
          mb={2}
          p={4}
          aria-live="polite"
          role="status"
          aria-atomic="true"
          boxShadow={{ base: "bottomLg", lg: "topLg" }}
        >
          <Flex w="100%" gap={4}>
            <Box w="100%">
              <Flex
                w="100%"
                gap={2}
                justify="center"
                align={{ base: "center", lg: "flex-start" }}
              >
                <Heading as="h2" size="lg">
                  {translate("budget.remainingbudget")}
                </Heading>
                <Tag
                  size="lg"
                  rounded="full"
                  variant="subtle"
                  colorPalette="blue"
                >
                  {formatBudget(remainingBudget || 0, unitCode)}
                </Tag>
              </Flex>

              <Flex direction="column" align="center" gap={1} mt={4}>
                <Box w="100%">
                  <Progress
                    borderRadius="md"
                    value={
                      totalBudget > 0
                        ? ((totalAllocated || 0) / totalBudget) * 100
                        : 0
                    }
                    css={{
                      "& [data-part=track]": {
                        h: "3"
                      },
                      "& [data-part=range]": {
                        transitionProperty: "width"
                      }
                    }}
                  />
                </Box>
                <Text fontSize="sm">
                  {formatBudget(totalAllocated || 0, unitCode)} /{" "}
                  {formatBudget(totalBudget || 0, unitCode)}{" "}
                  {translate("budget.allocated")}
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Box>
      ) : null}
      <Box>{children}</Box>
    </Flex>
  );
};

export default BudgetAllocation;
